<template>
    <div ref="rootElement" class="crime-layer" style="display: none;"></div>
</template>

<script lang="ts">
import {
    attributionContent,
    borrowsReferenceIndex,
    CATEGORY_LABELS,
    colorForIndex,
    CrimeArea,
    detailsContent,
    escapeHtml,
    formatDate,
    formatNumber,
    hasWholeIncidents,
    incidentDensity,
    legendContent,
    paintedIndex,
} from '@/components/crime/crimePanel';
import {acquireLeafletMap, AcquiredMap} from '@/shared/leafletMapRegistry';
import {registerMinZoom, unregisterMinZooms} from '@/shared/layersControlMinZoom';
import L from 'leaflet';
import {defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

interface CrimeIncidentCluster {
    readonly segmentId: number;
    readonly latitude: number;
    readonly longitude: number;
    readonly incidentCount: number;
    readonly periodFrom: string;
}

interface CrimeIncident {
    readonly id: number;
    readonly occurredAt: string;
    readonly state: string;
    readonly locationRelevance: string;
    readonly reportedByMunicipality: boolean;
    readonly category: string | null;
    readonly offenceTypes: readonly string[];
}

interface CrimeIncidentPage {
    readonly segmentId: number;
    readonly periodFrom: string;
    readonly totalCount: number;
    readonly page: number;
    readonly pageCount: number;
    readonly incidents: readonly CrimeIncident[];
}

type Level = 'town' | 'settlement_unit';
type AreaLayer = L.Path & {feature?: GeoJSON.Feature; getBounds?: () => L.LatLngBounds; _containsPoint?: (point: L.Point) => boolean};

const OVERLAY_LABEL = 'Index kriminality';
// The overlay sits in a section of its own in the layers control, above the flood map.
const SECTION_TITLE = 'Kriminalita';
const SECTION_HEADER_CLASS = 'crime-layer-header';
const FLOOD_SECTION_HEADER_CLASS = 'flood-zone-header';
const PANE_NAME = 'crime';
const PANE_Z_INDEX = '230';
// The details panel keeps this much of the map free below it and never shrinks below the minimum.
const PANEL_BOTTOM_MARGIN = 10;
const PANEL_MINIMUM_HEIGHT = 120;
const BOUNDS_PADDING = 0.25;

// Nothing below zoom 11: a wider viewport means thousands of blurred polygons and megabytes per
// move (zoom 8 = the whole country, 6 258 towns, 15 MB). Towns at 11, settlement units from 12,
// where a city splits into readable neighbourhoods.
const MIN_ZOOM = 11;
const SETTLEMENT_UNIT_MIN_ZOOM = 12;
// Opened on a place, the view is fitted to its settlement unit: no closer than the street zoom for
// a small unit in a city, and a fifth of the unit's size as a margin around it.
const FOCUS_MAX_ZOOM = 16;
const FOCUS_MARGIN = 0.2;
// Individual incidents (clustered by anonymisation segment) from the street zoom.
const INCIDENT_MIN_ZOOM = 14;

// The boundaries of the statistical units are not where crime stops, so the fill is blurred and
// drawn without an outline, with one opacity for every area: the colour alone carries the value.
// The reliability of an area is explained in the panel, not by a paler fill, so that two
// neighbours with similar indexes never look different.
const FILL_OPACITY = 0.6;
// The area under the cursor gets a stronger fill of the same blurred polygon.
const HOVER_FILL_OPACITY_BOOST = 0.25;
// The place the map was opened on: a dot in the colour of the estate markers of the map.
const FOCUS_MARKER_STYLE: L.CircleMarkerOptions = {radius: 7, weight: 3, color: '#ffffff', fillColor: '#174c6c', fillOpacity: 1, interactive: false};

const PANEL_TITLE = 'Kriminalita';
// Shown in place of the overlay while the map is zoomed out too far for it.
const ZOOM_HINT = 'Pro zobrazení kriminality přibližte mapu';
const PANEL_HINT = 'Najeďte na oblast pro podrobnosti, kliknutím ji podržíte.';
const TOUCH_HINT = 'Klepněte na oblast pro podrobnosti.';
const INCIDENT_HINT = 'Kolečka s počtem jsou skutky posledních 12 měsíců, kliknutím je zobrazíte.';
// Page size of the incident listing; the popup shows one incident at a time and fetches pages as needed.
const INCIDENT_PAGE_SIZE = 10;

// Accent colour of the incident card by category (the index categories in the map's warm tones).
const CATEGORY_COLORS: Record<string, string> = {
    violent: '#bd0026',
    burglary: '#f03b20',
    vehicle_theft: '#fd8d3c',
    theft: '#e6a100',
    drugs: '#7b4fa3',
    property_offence: '#5b7c99',
    public_order_offence: '#6c757d',
    other: '#6c757d',
};
// Inline icons: no dependency on the icon font inside a Leaflet popup, and they centre exactly.
const CHEVRON_LEFT = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHEVRON_RIGHT = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const STATE_ICONS: Record<string, string> = {
    suspect_identified: '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path d="M5 12l5 5 9-10" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    unsolved: '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path d="M9 9a3 3 0 1 1 4.5 2.6c-1 .6-1.5 1.3-1.5 2.4M12 18h.01" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    did_not_happen: '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path d="M6 12h12" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"/></svg>',
    not_an_offence: '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path d="M6 12h12" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"/></svg>',
};

const STATE_LABELS: Record<string, string> = {
    suspect_identified: 'zjištěn pravděpodobný pachatel',
    unsolved: 'neobjasněno',
    did_not_happen: 'skutek se nestal',
    not_an_offence: 'není trestným činem',
};
const RELEVANCE_LABELS: Record<string, string> = {
    place_of_commission: 'místo spáchání',
    place_of_consequence: 'místo následku',
    place_of_report: 'místo oznámení',
    abroad: 'v zahraničí',
};

function levelForZoom(zoom: number): Level | null {
    if (zoom < MIN_ZOOM) {
        return null;
    }
    return zoom >= SETTLEMENT_UNIT_MIN_ZOOM ? 'settlement_unit' : 'town';
}

// Blur radius in pixels: a soft edge at the town level, a wider one where units are large on screen.
function blurRadiusForZoom(zoom: number): number {
    if (zoom < SETTLEMENT_UNIT_MIN_ZOOM) {
        return 4;
    }
    return zoom < 14 ? 6 : 8;
}

// The polygons never take pointer events themselves: the cadastral units above them would swallow
// the events anyway, so hovering and clicking are resolved by a point-in-polygon test on map events.
function areaStyle(area: CrimeArea): L.PathOptions {
    const index = paintedIndex(area);
    if (index === null) {
        return {stroke: false, fill: false, interactive: false};
    }
    return {
        stroke: false,
        interactive: false,
        fillColor: colorForIndex(index),
        fillOpacity: FILL_OPACITY,
    };
}

function formatDateTime(isoDateTime: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})$/.exec(isoDateTime);
    if (!match) {
        return isoDateTime;
    }
    return `${Number(match[3])}. ${Number(match[2])}. ${match[1]} ${match[4]}`;
}

function clusterSize(count: number): string {
    if (count >= 50) {
        return 'large';
    }
    return count >= 10 ? 'medium' : 'small';
}

// One incident of a segment as a card: category stripe, the offence types, the state and date,
// and large previous/next controls, the way the Police portal browses the incidents of a place.
function incidentContent(incident: CrimeIncident, position: number, totalCount: number, periodFrom: string): string {
    const categoryKey = incident.category ?? 'other';
    const category = incident.category !== null ? (CATEGORY_LABELS[incident.category] ?? incident.category) : 'Nezařazeno';
    const color = CATEGORY_COLORS[categoryKey] ?? CATEGORY_COLORS.other;
    const typeItems = incident.offenceTypes.map((type) => {
        const parts = type.split(' › ');
        const leaf = escapeHtml(parts[parts.length - 1]);
        const group = parts.length > 1 ? `<span class="crime-incident__group">${escapeHtml(parts[0])}</span>` : '';
        return `<li>${group}<span class="crime-incident__leaf">${leaf}</span></li>`;
    });
    const types = typeItems.length > 0 ? typeItems.join('') : '<li><span class="crime-incident__leaf">neuvedený typ skutku</span></li>';
    const place = [
        RELEVANCE_LABELS[incident.locationRelevance] ?? null,
        incident.reportedByMunicipality ? 'oznámila obecní policie' : null,
    ].filter((part): part is string => part !== null);
    const stateLabel = STATE_LABELS[incident.state] ?? incident.state;

    return `<div class="crime-incident" style="--crime-incident-color:${color}">`
        + `<div class="crime-incident__head"><span class="crime-incident__category">${escapeHtml(category)}</span>`
        + `<span class="crime-incident__counter">${position} z ${totalCount}</span></div>`
        + `<ul class="crime-incident__types">${types}</ul>`
        + `<dl class="crime-incident__facts">`
        + `<dt>Kdy</dt><dd><span class="crime-incident__value">${formatDateTime(incident.occurredAt)}</span></dd>`
        + `<dt>Objasnění</dt><dd><span class="crime-incident__state crime-incident__state--${incident.state}">${STATE_ICONS[incident.state] ?? ''}</span>`
        + `<span class="crime-incident__value">${stateLabel}</span></dd>`
        + `<dt>Místo</dt><dd><span class="crime-incident__value">${place.length > 0 ? place.join(', ') : 'neuvedeno'}</span></dd>`
        + `</dl>`
        + `<div class="crime-incident__nav">`
        + `<button type="button" class="crime-incident__step" data-position="${position - 1}"${position <= 1 ? ' disabled' : ''} aria-label="Předchozí skutek">${CHEVRON_LEFT}</button>`
        + `<span class="crime-incident__nav-text">skutky od ${formatDate(periodFrom)}<br/>poloha anonymizovaná na bod zóny</span>`
        + `<button type="button" class="crime-incident__step" data-position="${position + 1}"${position >= totalCount ? ' disabled' : ''} aria-label="Další skutek">${CHEVRON_RIGHT}</button>`
        + `</div>`
        + `</div>`;
}

function tooltipContent(area: CrimeArea): string {
    if (borrowsReferenceIndex(area)) {
        const density = incidentDensity(area);
        return `${escapeHtml(area.name)}${density !== null && hasWholeIncidents(area) ? ` · ${formatNumber(density, density < 10 ? 1 : 0)} skutků na km²` : ''}`;
    }
    const index = area.indexValue !== null ? ` · index ${formatNumber(area.indexValue, 0)}` : '';
    return `${escapeHtml(area.name)}${index}`;
}

// A map in a dialog that is not shown yet has no size: nothing is loaded for it until it gets one.
function hasSize(map: L.Map): boolean {
    const size = map.getSize();
    return size.x > 0 && size.y > 0;
}

function supportsHover(): boolean {
    return typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches;
}

export default defineComponent({
    props: {
        loadAreasUrl: {
            type: String,
            required: true,
        },
        loadIncidentClustersUrl: {
            type: String,
            required: true,
        },
        loadSegmentIncidentsUrl: {
            type: String,
            required: true,
        },
        // On a map of its own the overlay is its content: always shown, with no row in the layers control.
        permanent: {
            type: Boolean,
            default: false,
        },
        // The place the map was opened on (from the crime profile of an estate), or null.
        focus: {
            type: Object as PropType<{latitude: number; longitude: number} | null>,
            default: null,
        },
    },
    setup(props) {
        const rootElement = ref<HTMLElement | null>(null);

        let map: L.Map | null = null;
        let layersControl: L.Control.Layers | null = null;
        let originalLayersControlUpdate: (() => unknown) | null = null;
        let panel: L.Control | null = null;
        let panelElement: HTMLElement | null = null;
        let zoomHint: L.Control | null = null;
        let pane: HTMLElement | null = null;
        let geoJson: L.GeoJSON | null = null;
        let clusters: L.LayerGroup | null = null;
        const clusterMarkers = new Map<number, L.Marker>();
        const incidentPages = new Map<string, CrimeIncidentPage>();
        let pinnedArea: CrimeArea | null = null;
        let hoveredArea: CrimeArea | null = null;
        // The panel folded to a button by the user; a pinned area still opens it.
        let panelFolded = false;
        let highlightedLayer: AreaLayer | null = null;
        let tooltip: L.Tooltip | null = null;
        let loadedBounds: L.LatLngBounds | null = null;
        let loadedLevel: Level | null = null;
        let loadedClusterBounds: L.LatLngBounds | null = null;
        let abortController: AbortController | null = null;
        let clusterAbortController: AbortController | null = null;
        const hoverSupported = supportsHover();
        // The place the map was opened on until its area is picked, and its marker.
        let focusToPick: L.LatLng | null = null;
        let focusMarker: L.CircleMarker | null = null;

        // The panel scrolls inside the map: its height is what is left of the map below its top
        // edge (the layers control above it may grow when expanded), not a share of the viewport.
        function updatePanelHeight(): void {
            if (!map || !panelElement || !panelElement.isConnected) {
                return;
            }
            const top = panelElement.getBoundingClientRect().top - map.getContainer().getBoundingClientRect().top;
            panelElement.style.maxHeight = `${Math.max(PANEL_MINIMUM_HEIGHT, map.getSize().y - top - PANEL_BOTTOM_MARGIN)}px`;
        }

        function renderPanel(area: CrimeArea | null): void {
            if (!panelElement) {
                return;
            }
            updatePanelHeight();
            const pinned = area !== null && pinnedArea !== null && pinnedArea.unitCode === area.unitCode;
            if (panelFolded && !pinned) {
                panelElement.classList.add('crime-panel--folded');
                panelElement.innerHTML = `<button type="button" class="crime-panel__unfold" title="Zobrazit legendu a podrobnosti">${PANEL_TITLE}</button>`;
                onPanelButton('.crime-panel__unfold', () => {
                    panelFolded = false;
                    renderPanel(hoveredArea);
                });
                return;
            }
            panelElement.classList.remove('crime-panel--folded');
            if (area === null) {
                const incidentHint = map !== null && map.getZoom() >= INCIDENT_MIN_ZOOM ? ` ${INCIDENT_HINT}` : '';
                panelElement.innerHTML = `<button type="button" class="crime-details__close" title="Sbalit panel (Esc)">×</button>`
                    + `<div class="crime-details__hint">${hoverSupported ? PANEL_HINT : TOUCH_HINT}${incidentHint}</div>`
                    + legendContent() + attributionContent();
                onPanelButton('.crime-details__close', foldPanel);
                return;
            }
            const close = pinned ? '<button type="button" class="crime-details__close" title="Uvolnit (Esc)">×</button>' : '';
            panelElement.innerHTML = `${close}<div class="crime-details">${detailsContent(area)}</div>`;
            onPanelButton('.crime-details__close', unpin);
        }

        // A button of the panel: the click must not reach the map. Re-rendering the panel removes
        // the button from the document before the event bubbles to Leaflet, which then cannot
        // tell the click came from the panel and would pin the area under the cursor.
        function onPanelButton(selector: string, handler: () => void): void {
            const button = panelElement?.querySelector(selector);
            if (button) {
                L.DomEvent.on(button as HTMLElement, 'click', (event) => {
                    L.DomEvent.stop(event);
                    handler();
                });
            }
        }

        function unpin(): void {
            pinnedArea = null;
            showHighlight(null);
            renderPanel(null);
        }

        function foldPanel(): void {
            panelFolded = true;
            renderPanel(null);
        }

        // Escape releases the pinned area, or folds the panel when nothing is pinned.
        function onKeyDown(event: KeyboardEvent): void {
            if (event.key !== 'Escape' || !panelElement || !panelElement.isConnected) {
                return;
            }
            if (pinnedArea !== null) {
                unpin();
            } else if (!panelFolded) {
                foldPanel();
            }
        }

        function showHighlight(layer: AreaLayer | null): void {
            if (highlightedLayer !== null && highlightedLayer !== layer) {
                const previous = areaOf(highlightedLayer);
                if (previous !== null) {
                    highlightedLayer.setStyle(areaStyle(previous));
                }
            }
            highlightedLayer = layer;
            if (layer !== null) {
                const area = areaOf(layer);
                const style = area !== null ? areaStyle(area) : {};
                layer.setStyle({...style, fillOpacity: Math.min(1, (style.fillOpacity ?? 0) + HOVER_FILL_OPACITY_BOOST)});
            }
        }

        function isPointInLayer(layer: L.Layer, latlng: L.LatLng): boolean {
            if (!map) {
                return false;
            }
            const path = layer as L.Layer & {_containsPoint?: (point: L.Point) => boolean};
            return path._containsPoint !== undefined && path._containsPoint(map.latLngToLayerPoint(latlng));
        }

        function findLayerAt(latlng: L.LatLng): AreaLayer | null {
            if (!map || !geoJson || !map.hasLayer(geoJson)) {
                return null;
            }
            let hit: AreaLayer | null = null;
            geoJson.eachLayer((layer) => {
                if (hit !== null) {
                    return;
                }
                const path = layer as AreaLayer;
                if (path.getBounds === undefined || !path.getBounds().contains(latlng) || !isPointInLayer(layer, latlng)) {
                    return;
                }
                hit = path;
            });
            return hit;
        }

        function areaOf(layer: AreaLayer | null): CrimeArea | null {
            return (layer?.feature?.properties as CrimeArea | undefined) ?? null;
        }

        function showTooltip(area: CrimeArea | null, latlng: L.LatLng): void {
            if (!map) {
                return;
            }
            if (area === null) {
                tooltip?.close();
                return;
            }
            if (!tooltip) {
                tooltip = L.tooltip({direction: 'top', offset: [0, -8], className: 'crime-tooltip'});
            }
            tooltip.setLatLng(latlng).setContent(tooltipContent(area));
            if (!tooltip.isOpen()) {
                tooltip.openOn(map);
            }
        }

        function onMapMouseMove(event: L.LeafletMouseEvent): void {
            const layer = findLayerAt(event.latlng);
            const area = areaOf(layer);
            showTooltip(area, event.latlng);
            if (pinnedArea !== null) {
                return;
            }
            if (area?.unitCode === hoveredArea?.unitCode) {
                return;
            }
            hoveredArea = area;
            showHighlight(layer);
            renderPanel(area);
        }

        function onMapMouseOut(): void {
            tooltip?.close();
            if (pinnedArea !== null || hoveredArea === null) {
                return;
            }
            hoveredArea = null;
            showHighlight(null);
            renderPanel(null);
        }

        function onMapClick(event: L.LeafletMouseEvent): void {
            selectAreaAt(event.latlng);
        }

        // Pins the area at a place for the panel, or opens its details in a popup where the device
        // cannot hover.
        function selectAreaAt(latlng: L.LatLng): void {
            if (!map) {
                return;
            }
            const layer = findLayerAt(latlng);
            const area = areaOf(layer);
            if (!hoverSupported) {
                if (area !== null) {
                    L.popup({className: 'crime-popup', maxWidth: 320})
                        .setLatLng(latlng)
                        .setContent(`<div class="crime-details">${detailsContent(area)}</div>`)
                        .openOn(map);
                }
                return;
            }
            pinnedArea = area;
            hoveredArea = area;
            showHighlight(layer);
            renderPanel(area);
        }

        async function fetchIncidentPage(segmentId: number, page: number): Promise<CrimeIncidentPage | null> {
            const key = `${segmentId}:${page}`;
            const cached = incidentPages.get(key);
            if (cached) {
                return cached;
            }
            // A network failure is reported the same way as an error response: the caller shows
            // the message instead of leaving the popup on its loading text.
            try {
                const response = await fetch(props.loadSegmentIncidentsUrl, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({segmentId, page}),
                });
                if (!response.ok) {
                    return null;
                }
                const loaded = await response.json() as CrimeIncidentPage;
                incidentPages.set(key, loaded);
                return loaded;
            } catch {
                return null;
            }
        }

        // Shows the incident at the 1-based position of the newest-first listing of the segment.
        async function showIncident(popup: L.Popup, segmentId: number, position: number): Promise<void> {
            const page = await fetchIncidentPage(segmentId, Math.ceil(position / INCIDENT_PAGE_SIZE));
            if (page === null) {
                popup.setContent('<div class="text-danger">Skutky se nepodařilo načíst.</div>');
                return;
            }
            const incident = page.incidents[(position - 1) % INCIDENT_PAGE_SIZE];
            if (incident === undefined) {
                popup.setContent('<div class="crime-details__meta">Žádné skutky za posledních 12 měsíců.</div>');
                return;
            }
            popup.setContent(incidentContent(incident, position, page.totalCount, page.periodFrom));
            popup.getElement()?.querySelectorAll<HTMLButtonElement>('.crime-incident__step').forEach((button) => {
                button.addEventListener('click', (event) => {
                    // The content is replaced while this click is still being dispatched; without
                    // stopping it here the map would see a click outside the (old) popup and close it.
                    L.DomEvent.stop(event);
                    void showIncident(popup, segmentId, Number(button.dataset.position));
                });
            });
        }

        function clusterMarker(cluster: CrimeIncidentCluster): L.Marker {
            const marker = L.marker([cluster.latitude, cluster.longitude], {
                icon: L.divIcon({
                    className: `crime-cluster crime-cluster--${clusterSize(cluster.incidentCount)}`,
                    html: `<span>${cluster.incidentCount}</span>`,
                    iconSize: undefined,
                }),
                title: `${cluster.incidentCount} skutků od ${formatDate(cluster.periodFrom)}`,
            });
            marker.bindPopup('<div class="crime-details__meta p-2">Načítám skutky…</div>', {className: 'crime-popup crime-popup--incident', minWidth: 300, maxWidth: 300, autoPanPadding: [20, 80]});
            marker.on('popupopen', (event) => {
                void showIncident(event.popup, cluster.segmentId, 1);
            });
            return marker;
        }

        // Keeps the markers of segments that are still in the viewport (an open popup survives
        // a reload), adds the new ones and drops those that left, apart from one with an open popup.
        function syncClusterMarkers(loaded: CrimeIncidentCluster[]): void {
            if (!clusters) {
                return;
            }
            const loadedIds = new Set(loaded.map((cluster) => cluster.segmentId));
            clusterMarkers.forEach((marker, segmentId) => {
                if (!loadedIds.has(segmentId) && !marker.isPopupOpen()) {
                    clusters?.removeLayer(marker);
                    clusterMarkers.delete(segmentId);
                }
            });
            loaded.forEach((cluster) => {
                if (clusterMarkers.has(cluster.segmentId)) {
                    return;
                }
                const marker = clusterMarker(cluster);
                clusterMarkers.set(cluster.segmentId, marker);
                clusters?.addLayer(marker);
            });
        }

        async function loadClusters(): Promise<void> {
            if (!map || !clusters || !geoJson || !map.hasLayer(geoJson) || !hasSize(map)) {
                return;
            }

            if (map.getZoom() < INCIDENT_MIN_ZOOM) {
                clusters.clearLayers();
                clusterMarkers.clear();
                loadedClusterBounds = null;
                return;
            }

            const bounds = map.getBounds();
            if (loadedClusterBounds && loadedClusterBounds.contains(bounds)) {
                return;
            }

            const paddedBounds = bounds.pad(BOUNDS_PADDING);
            clusterAbortController?.abort();
            const controller = new AbortController();
            clusterAbortController = controller;

            try {
                const response = await fetch(props.loadIncidentClustersUrl, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        boundingBox: {
                            southWest: {longitude: paddedBounds.getWest(), latitude: paddedBounds.getSouth()},
                            northEast: {longitude: paddedBounds.getEast(), latitude: paddedBounds.getNorth()},
                        },
                        zoom: map.getZoom(),
                    }),
                    signal: controller.signal,
                });

                if (!response.ok) {
                    return;
                }

                const loaded: CrimeIncidentCluster[] = await response.json();
                syncClusterMarkers(loaded);
                loadedClusterBounds = paddedBounds;
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    loadedClusterBounds = null;
                }
            }
        }

        function createGeoJson(): L.GeoJSON {
            return L.geoJSON(undefined, {
                pane: PANE_NAME,
                style: (feature) => areaStyle(feature?.properties as CrimeArea),
            });
        }

        function updateBlur(): void {
            if (!map || !pane) {
                return;
            }
            pane.style.filter = `blur(${blurRadiusForZoom(map.getZoom())}px)`;
        }

        async function load(): Promise<void> {
            if (!map || !geoJson || !map.hasLayer(geoJson) || !hasSize(map)) {
                return;
            }

            const level = levelForZoom(map.getZoom());
            if (level === null) {
                geoJson.clearLayers();
                loadedBounds = null;
                loadedLevel = null;
                return;
            }

            const bounds = map.getBounds();
            if (loadedLevel === level && loadedBounds && loadedBounds.contains(bounds)) {
                return;
            }

            const paddedBounds = bounds.pad(BOUNDS_PADDING);
            abortController?.abort();
            const controller = new AbortController();
            abortController = controller;

            try {
                const response = await fetch(props.loadAreasUrl.replace('__LEVEL__', encodeURIComponent(level)), {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        boundingBox: {
                            southWest: {longitude: paddedBounds.getWest(), latitude: paddedBounds.getSouth()},
                            northEast: {longitude: paddedBounds.getEast(), latitude: paddedBounds.getNorth()},
                        },
                        zoom: map.getZoom(),
                    }),
                    signal: controller.signal,
                });

                if (!response.ok) {
                    return;
                }

                const areas: CrimeArea[] = await response.json();
                const collection: GeoJSON.FeatureCollection = {
                    type: 'FeatureCollection',
                    features: areas.map((area) => ({
                        type: 'Feature',
                        geometry: area.geometry,
                        properties: area,
                    })),
                };

                highlightedLayer = null;
                geoJson.clearLayers();
                geoJson.addData(collection);
                loadedBounds = paddedBounds;
                loadedLevel = level;

                // The layers are new: re-apply the highlight of the pinned area, or release a
                // pinned area of another level that is gone from the map.
                if (pinnedArea !== null && pinnedArea.level !== level) {
                    pinnedArea = null;
                    hoveredArea = null;
                    renderPanel(null);
                } else if (pinnedArea !== null) {
                    const pinnedCode = pinnedArea.unitCode;
                    geoJson.eachLayer((layer) => {
                        if (areaOf(layer as AreaLayer)?.unitCode === pinnedCode) {
                            showHighlight(layer as AreaLayer);
                        }
                    });
                }

                // Opened on a place: its area is picked once the settlement units are in, and the
                // view is fitted to it, so that a village unit shows whole and a city block close.
                if (focusToPick !== null && level === 'settlement_unit') {
                    const place = focusToPick;
                    focusToPick = null;
                    selectAreaAt(place);
                    fitFocusedArea(place);
                }
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    loadedBounds = null;
                }
            }
        }

        function buildPanel(): L.Control {
            const control = new L.Control({position: 'topright'});
            control.onAdd = () => {
                const container = L.DomUtil.create('div', 'crime-panel');
                L.DomEvent.disableClickPropagation(container);
                L.DomEvent.disableScrollPropagation(container);
                // The map listens for mouse moves on its whole container, the panel included: a cursor
                // over the panel must not pick the areas underneath it, and the tooltip of the last
                // area goes away.
                L.DomEvent.on(container, 'mousemove', L.DomEvent.stopPropagation);
                L.DomEvent.on(container, 'mouseenter', () => tooltip?.close());
                panelElement = container;
                renderPanel(pinnedArea);
                return container;
            };
            control.onRemove = () => {
                panelElement = null;
            };
            return control;
        }

        // The hint that the map is zoomed out too far for the overlay, the way the cadastral layer has one.
        function buildZoomHint(): L.Control {
            const control = new L.Control({position: 'bottomleft'});
            control.onAdd = () => {
                const container = L.DomUtil.create('div', 'leaflet-bar crime-zoom-hint');
                container.textContent = ZOOM_HINT;
                return container;
            };
            return control;
        }

        function updateControlsVisibility(): void {
            if (!map || !panel || !zoomHint || !geoJson) {
                return;
            }
            const shown = map.hasLayer(geoJson);
            if (shown && map.getZoom() >= MIN_ZOOM) {
                panel.addTo(map);
                clusters?.addTo(map);
                if (pinnedArea === null) {
                    renderPanel(null);
                }
                updatePanelHeight();
            } else {
                panel.remove();
                clusters?.remove();
                tooltip?.close();
                pinnedArea = null;
                hoveredArea = null;
                showHighlight(null);
            }
            // Adding a control that is on the map already just moves it, so this is safe to repeat.
            if (shown && map.getZoom() < MIN_ZOOM) {
                zoomHint.addTo(map);
            } else {
                zoomHint.remove();
            }
        }

        function onMapChange(): void {
            updateControlsVisibility();
            updateBlur();
            void load();
            void loadClusters();
        }

        function initLayer(): void {
            if (!map) {
                return;
            }

            pane = map.createPane(PANE_NAME);
            pane.style.zIndex = PANE_Z_INDEX;
            updateBlur();

            geoJson = createGeoJson();
            clusters = L.layerGroup();
            if (layersControl && !props.permanent) {
                layersControl.addOverlay(geoJson, OVERLAY_LABEL);
                registerMinZoom(map, layersControl, geoJson, MIN_ZOOM);
                patchLayersControlUpdate();
                placeOverlayRow();
            }

            panel = buildPanel();
            zoomHint = buildZoomHint();

            map.on('moveend', onMapChange);
            map.on('resize', updatePanelHeight);
            document.addEventListener('keydown', onKeyDown);
            map.on('overlayadd', onMapChange);
            map.on('overlayremove', onMapChange);
            map.on('click', onMapClick);
            if (hoverSupported) {
                map.on('mousemove', onMapMouseMove);
                map.on('mouseout', onMapMouseOut);
            }

            if (props.focus !== null) {
                markFocus(L.latLng(props.focus.latitude, props.focus.longitude));
            }
            if (props.permanent || props.focus !== null) {
                map.addLayer(geoJson);
                onMapChange();
            }
        }

        // The place stays in the middle of the map; the zoom is the one that would show the whole
        // area of the place with a margin around it, between the zoom the settlement units are drawn
        // from and the street zoom, whatever the size of the area.
        function fitFocusedArea(place: L.LatLng): void {
            const layer = map ? findLayerAt(place) : null;
            if (!map || layer === null || layer.getBounds === undefined) {
                return;
            }
            const zoom = Math.min(FOCUS_MAX_ZOOM, Math.max(SETTLEMENT_UNIT_MIN_ZOOM, map.getBoundsZoom(layer.getBounds().pad(FOCUS_MARGIN))));
            map.setView(place, zoom, {animate: false});
        }

        // Opened on a place (from the crime profile of an estate): the place is marked and its area
        // picked as soon as the settlement units are loaded.
        function markFocus(place: L.LatLng): void {
            if (!map) {
                return;
            }
            focusToPick = place;
            focusMarker = L.circleMarker(place, FOCUS_MARKER_STYLE).addTo(map);
        }

        // The layers control lists overlays in the order they were added, which would put this one
        // under the zoning plan. After every rebuild of the list the row moves under a header of its
        // own, above the flood map (or to the end when there is no flood map).
        function placeOverlayRow(): void {
            if (!layersControl || !geoJson) {
                return;
            }
            const overlaysList = (layersControl as unknown as {_overlaysList?: HTMLElement})._overlaysList;
            if (!overlaysList) {
                return;
            }
            const layerId = L.Util.stamp(geoJson);
            const row = Array.from(overlaysList.querySelectorAll('label')).find((label) => {
                const input = label.querySelector('input') as (HTMLInputElement & {layerId?: number}) | null;
                return input?.layerId === layerId;
            });
            if (!row) {
                return;
            }
            const header = document.createElement('div');
            header.className = SECTION_HEADER_CLASS;
            header.textContent = SECTION_TITLE;
            const anchor = overlaysList.querySelector(`.${FLOOD_SECTION_HEADER_CLASS}`);
            overlaysList.insertBefore(header, anchor);
            overlaysList.insertBefore(row, anchor);
        }

        // The layers control rebuilds its list on every change, so the row is placed again after each.
        function patchLayersControlUpdate(): void {
            const control = layersControl as unknown as {_update?: () => unknown} | null;
            if (!control?._update) {
                return;
            }
            const original = control._update;
            originalLayersControlUpdate = original;
            control._update = (): unknown => {
                const result = original.call(layersControl);
                placeOverlayRow();
                return result;
            };
        }

        function restoreLayersControlUpdate(): void {
            if (layersControl && originalLayersControlUpdate) {
                (layersControl as unknown as {_update?: () => unknown})._update = originalLayersControlUpdate;
                originalLayersControlUpdate = null;
            }
        }

        function detachFromMap(): void {
            if (map) {
                restoreLayersControlUpdate();
                map.off('moveend', onMapChange);
                map.off('resize', updatePanelHeight);
                document.removeEventListener('keydown', onKeyDown);
                map.off('overlayadd', onMapChange);
                map.off('overlayremove', onMapChange);
                map.off('click', onMapClick);
                map.off('mousemove', onMapMouseMove);
                map.off('mouseout', onMapMouseOut);
                tooltip?.close();
                if (layersControl && geoJson) {
                    unregisterMinZooms(layersControl, [geoJson]);
                }
                abortController?.abort();
                clusterAbortController?.abort();
                if (geoJson) {
                    map.removeLayer(geoJson);
                }
                if (clusters) {
                    map.removeLayer(clusters);
                }
                if (focusMarker) {
                    map.removeLayer(focusMarker);
                }
                panel?.remove();
                zoomHint?.remove();
            }
            geoJson = null;
            clusters = null;
            clusterMarkers.clear();
            incidentPages.clear();
            highlightedLayer = null;
            loadedClusterBounds = null;
            panel = null;
            panelElement = null;
            zoomHint = null;
            pane = null;
            tooltip = null;
            pinnedArea = null;
            hoveredArea = null;
            focusToPick = null;
            focusMarker = null;
            layersControl = null;
            map = null;
        }

        function attachToMap(acquired: AcquiredMap): void {
            map = acquired.map;
            layersControl = acquired.layersControl;
            initLayer();
        }

        let cleanupMapListener: (() => void) | null = null;

        onMounted(() => {
            cleanupMapListener = acquireLeafletMap(rootElement.value!, attachToMap);
        });

        onBeforeUnmount(() => {
            cleanupMapListener?.();
            detachFromMap();
        });

        return {rootElement};
    },
});
</script>
